import { useEffect, useMemo, useState } from 'react';
import { onSnapshot } from 'firebase/firestore';
import { Activity } from '../types';
import {
  activitiesForEntityQuery,
  activitiesRecentQuery,
  parseActivityDocs,
} from '../utils/activitiesQueries';

export interface UseActivitiesResult {
  activities: Activity[];
  loading: boolean;
}

/** Bounded recent activities — mount only on pages that need the global feed. */
export function useActivities(): UseActivitiesResult {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = activitiesRecentQuery();
    const unsub = onSnapshot(
      q,
      snapshot => {
        setActivities(parseActivityDocs(snapshot.docs));
        setLoading(false);
      },
      err => {
        console.warn('[useActivities] listener failed:', err);
        setActivities([]);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return useMemo(() => ({ activities, loading }), [activities, loading]);
}

/** Per-entity activity history — lead/deal/project panels and messages. */
export function useActivitiesForEntity(
  entityType: 'lead' | 'deal' | 'project' | undefined,
  entityId: string | undefined,
): UseActivitiesResult {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityType || !entityId) {
      setActivities([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = activitiesForEntityQuery(entityType, entityId);
    const unsub = onSnapshot(
      q,
      snapshot => {
        setActivities(parseActivityDocs(snapshot.docs));
        setLoading(false);
      },
      err => {
        console.warn('[useActivitiesForEntity] listener failed:', err);
        setActivities([]);
        setLoading(false);
      },
    );
    return unsub;
  }, [entityType, entityId]);

  return useMemo(() => ({ activities, loading }), [activities, loading]);
}

/** Client-side filter helper (replaces CRMContext.getActivitiesForEntity). */
export function filterActivitiesForEntity(
  activities: Activity[],
  entityType: 'lead' | 'deal' | 'project',
  entityId: string,
): Activity[] {
  return activities.filter(a => a.entityType === entityType && a.entityId === entityId);
}
